import {Tooltip, Bar, BarChart, CartesianGrid, ResponsiveContainer, XAxis, YAxis} from 'recharts'
import Text from '../text/Text'
import {SocketSubMessage} from '../../pages/backend/logStream'
import {useMemo} from 'react'

// warn is the baseline noise (neutral series blue), err is the thing to catch (critical red)
export const WARN_COLOR = '#2a78d6'
export const ERR_COLOR = '#d03b3b'

const AXIS_INK = '#898781'
const GRID_INK = '#e1e0d9'
const LABEL_INK = '#52514e'

export interface dataSet {
    // local time label shown on the x axis / tooltip
    name: string
    // raw UTC bucket (YYYYMMDDHH) the API returned, used to look the hour back up
    daytime: string
    warn_count: number
    err_count: number
}

export interface IBarChartProps {
    title?: string
    totalCount?: string
    dataset: dataSet[]
    onClick?: (e: any) => void
}

export const compactCount = (value: number): string => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(value % 1000000 === 0 ? 0 : 1)}M`
    if (value >= 1000) return `${(value / 1000).toFixed(value % 1000 === 0 ? 0 : 1)}k`
    return `${value}`
}

export const CBarChart: React.FC<IBarChartProps> = ({title, totalCount, onClick, dataset}) => {
    // the full 'MM/DD HH시' label is only worth the width once per day, the rest are hours
    const dayHeads = useMemo(() => {
        const days = new Set<string>()
        const heads = new Set<string>()
        for (const item of dataset) {
            const day = item.name.split(' ')[0]
            if (days.has(day)) continue
            days.add(day)
            heads.add(item.name)
        }
        return heads
    }, [dataset])

    const formatTick = (value: string) => (dayHeads.has(value) ? value : value.split(' ')[1] ?? value)

    const handleClick = (e: any) => {
        onClick({
            server_id: title,
            created_at: e.daytime,
        })
    }
    return (
        <div className="relative mb-[10px]">
            <div className="flex items-baseline gap-[8px] mx-[1rem] mb-[4px]">
                <Text children={title} className="font-bold" />
                <Text children={`total ${totalCount}`} className="text-body3 text-gray-500" />
            </div>
            <ResponsiveContainer height={130} minWidth="30vw" maxHeight={150}>
                <BarChart
                    maxBarSize={14}
                    data={dataset}
                    margin={{
                        top: 5,
                        right: 15,
                        left: 0,
                        bottom: 0,
                    }}>
                    <CartesianGrid vertical={false} stroke={GRID_INK} />
                    <Tooltip
                        cursor={{fill: 'rgba(11,11,11,0.04)'}}
                        contentStyle={{borderRadius: 6, border: `1px solid ${GRID_INK}`, fontSize: 12}}
                        labelStyle={{color: LABEL_INK, fontWeight: 700}}
                        formatter={(value: number) => value.toLocaleString()}
                    />
                    <XAxis
                        dataKey="name"
                        tickFormatter={formatTick}
                        minTickGap={4}
                        tickLine={false}
                        tick={{fill: AXIS_INK, fontSize: 10}}
                        stroke={GRID_INK}
                    />
                    <YAxis
                        width={44}
                        tickFormatter={compactCount}
                        tickLine={false}
                        axisLine={false}
                        tick={{fill: AXIS_INK, fontSize: 10}}
                    />
                    <Bar
                        onClick={handleClick}
                        name="warn"
                        dataKey="warn_count"
                        stackId="a"
                        radius={[2, 2, 0, 0]}
                        fill={WARN_COLOR}
                    />
                    <Bar
                        onClick={handleClick}
                        name="error"
                        dataKey="err_count"
                        stackId="a"
                        radius={[2, 2, 0, 0]}
                        fill={ERR_COLOR}
                    />
                </BarChart>
            </ResponsiveContainer>
        </div>
    )
}
